import { useEffect, useState } from 'react'
import { FileText, ShieldCheck, Sparkles, User as UserIcon } from 'lucide-react'

const FRASES = [
  'Bem-vindo ao futuro.',
  'Esqueça os papéis.',
  'Tudo digital, tudo rastreável.',
  'Menos burocracia. Mais resultado.',
]

/** Tempo que cada frase fica no ecrã, em milissegundos */
const TEMPO_FRASE = 2400

interface LoadingScreenProps {
  /** Texto fixo opcional mostrado acima das frases rotativas */
  titulo?: string
  /** Duração total em ms. Por omissão, todas as frases são mostradas. */
  duracao?: number
  /** Chamado quando a sequência de frases termina */
  onConcluido?: () => void
  /** Utilizador que acabou de entrar: mostra o avatar animado no canto superior */
  usuario?: { nome: string, fotoUrl?: string }
}

export function LoadingScreen({ titulo = 'PROTOCOLEAI', duracao, onConcluido, usuario }: LoadingScreenProps) {
  const total = duracao ?? FRASES.length * TEMPO_FRASE
  const quantidade = Math.max(1, Math.ceil(total / TEMPO_FRASE))
  const frases = FRASES.slice(0, Math.min(quantidade, FRASES.length))
  const [indice, setIndice] = useState(0)
  const [progresso, setProgresso] = useState(6)
  const [podeSair, setPodeSair] = useState(false)

  useEffect(() => {
    const rotacao = setInterval(() => {
      setIndice(i => (i + 1 < frases.length ? i + 1 : i))
    }, TEMPO_FRASE)
    return () => clearInterval(rotacao)
  }, [frases.length])

  useEffect(() => {
    const inicio = Date.now()
    const barra = setInterval(() => {
      const decorrido = Date.now() - inicio
      const pct = Math.min(97, (decorrido / total) * 97)
      setProgresso(p => (pct > p ? pct : p))
    }, 180)
    return () => clearInterval(barra)
  }, [total])

  useEffect(() => {
    const liberar = setTimeout(() => setPodeSair(true), 1200)
    return () => clearTimeout(liberar)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => onConcluido?.(), total)
    return () => clearTimeout(t)
  }, [total, onConcluido])

  return (
    <div className="fixed inset-0 z-[100] bg-[#0B1220] flex flex-col items-center justify-center overflow-hidden">
      {/* Grade discreta de fundo */}
      <div
        className="absolute inset-0 opacity-[0.16]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #1E4FBF 1px, transparent 1px), linear-gradient(to bottom, #1E4FBF 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(circle at 50% 45%, black 0%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 45%, black 0%, transparent 72%)',
        }}
      />

      {/* Halo sólido suave */}
      <div className="absolute w-[520px] h-[520px] rounded-full bg-[#12203A] blur-[90px] opacity-70" />

      {/* Avatar do utilizador no canto superior */}
      {usuario && (
        <div className="absolute top-5 right-5 sm:top-7 sm:right-7 flex flex-col items-center gap-2 animate-[avatarIn_0.7s_ease-out]">
          <div className="relative w-12 h-12 sm:w-14 sm:h-14">
            <span className="absolute inset-0 rounded-full border-2 border-[#2A5BD7]/70 animate-[ringPulse_2.2s_ease-out_infinite]" />
            <span className="absolute inset-0 rounded-full border border-[#3FBF8F]/60 animate-[ringPulse_2.2s_ease-out_infinite_0.7s]" />
            <div className="relative w-full h-full rounded-full overflow-hidden bg-[#16263F] border border-[#2A5BD7] flex items-center justify-center">
              {usuario.fotoUrl
                ? <img src={usuario.fotoUrl} alt={usuario.nome} className="w-full h-full object-cover" />
                : <UserIcon className="w-5 h-5 text-[#7FA6FF]" strokeWidth={1.8} />}
            </div>
          </div>
          <span className="max-w-[130px] truncate text-[11.5px] font-medium text-[#9BB4DE] animate-[nomeIn_0.9s_ease-out]">
            {usuario.nome.split(' ')[0]}
          </span>
        </div>
      )}

      <div className="relative flex flex-col items-center px-6">
        {/* Pilha de documentos animada */}
        <div className="relative w-[132px] h-[150px] mb-10">
          <div className="absolute inset-0" style={{ transform: 'rotate(-14deg) translate(-16px, 10px)' }}>
            <div className="w-full h-full rounded-xl border border-[#1E4FBF]/30 bg-[#101B2E] animate-[docFloat_3.4s_ease-in-out_infinite]" />
          </div>
          <div className="absolute inset-0" style={{ transform: 'rotate(9deg) translate(14px, 6px)' }}>
            <div className="w-full h-full rounded-xl border border-[#1E4FBF]/45 bg-[#13223A] animate-[docFloat_3.4s_ease-in-out_infinite_0.35s]" />
          </div>
          <div className="absolute inset-0 rounded-xl border border-[#2A5BD7] bg-[#16263F] shadow-[0_18px_40px_rgba(0,0,0,0.45)] flex flex-col gap-2 p-4 animate-[docFloat_3.4s_ease-in-out_infinite_0.7s]">
            <div className="flex items-center justify-between">
              <FileText className="w-5 h-5 text-[#7FA6FF]" strokeWidth={1.6} />
              <span className="text-[9px] font-semibold tracking-[0.14em] text-[#5B7BB5]">PDF</span>
            </div>
            <div className="h-[3px] w-full rounded-full bg-[#2A5BD7]/40" />
            <div className="h-[3px] w-[78%] rounded-full bg-[#2A5BD7]/30" />
            <div className="h-[3px] w-[88%] rounded-full bg-[#2A5BD7]/30" />
            <div className="h-[3px] w-[54%] rounded-full bg-[#2A5BD7]/20" />
            <div className="mt-auto flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#3FBF8F]" strokeWidth={2} />
              <span className="text-[9px] font-medium text-[#3FBF8F]">assinado</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={2} />
          <span className="text-[11px] font-semibold tracking-[0.22em] text-[#5B7BB5] uppercase">
            {titulo}
          </span>
        </div>

        {/* Frase rotativa */}
        <div className="h-[42px] flex items-center justify-center">
          <p
            key={indice}
            className="text-[22px] sm:text-[26px] font-semibold text-white tracking-tight text-center animate-[fraseIn_0.6s_ease-out]"
          >
            {frases[indice]}
          </p>
        </div>

        {/* Barra de progresso */}
        <div className="w-[240px] h-[3px] rounded-full bg-[#1B2A44] mt-6 overflow-hidden">
          <div
            className="h-full rounded-full bg-[#1E4FBF] transition-all duration-300 ease-out"
            style={{ width: `${progresso}%` }}
          />
        </div>

        <p className="text-[11.5px] text-[#5B7BB5] mt-4 tracking-wide">
          A carregar documentos e preparar o seu ambiente…
        </p>

        <button
          onClick={() => onConcluido?.()}
          className={`mt-7 px-4 py-2 rounded-lg text-[12.5px] font-medium border border-[#24365A] text-[#9BB4DE] hover:text-white hover:border-[#2A5BD7] hover:bg-[#12203A] transition-all duration-300 ${podeSair ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          Continuar agora
        </button>
      </div>

      <style>{`
        @keyframes docFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes fraseIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes avatarIn {
          from { opacity: 0; transform: scale(0.7) translateY(-8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes nomeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ringPulse {
          0% { transform: scale(1); opacity: 0.9; }
          70% { transform: scale(1.45); opacity: 0; }
          100% { transform: scale(1.45); opacity: 0; }
        }
      `}</style>
    </div>
  )
}

export default LoadingScreen
